import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Send, RotateCcw, CalendarClock, LifeBuoy } from "lucide-react";
import { Card, PageHeader, Btn, StatusPill, Empty, inputCls } from "../../components/dash/Kit";
import BetaNotice from "../../components/dash/BetaNotice";
import ManualPostActions from "../../components/dash/ManualPostActions";
import { MarkAsPosted, UndoMarkPosted } from "../../components/dash/MarkAsPosted";
import { api, errMessage } from "../../lib/api";

export default function Publishing() {
  const [queue, setQueue] = useState(null);
  const [busy, setBusy] = useState("");
  const [when, setWhen] = useState({});

  const load = () => api.get("/social/queue").then((r) => setQueue(r.data)).catch(() => setQueue(false));
  useEffect(() => {
    load();
  }, []);

  const act = async (id, fn, okMessage) => {
    setBusy(id);
    try {
      const { data } = await fn();
      toast.success(data?.message || okMessage);
      await load();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy("");
    }
  };

  if (queue === null) return <Card testId="publishing-loading">Loading your publishing queue…</Card>;
  if (queue === false) return <Card testId="publishing-error">We couldn't load your publishing queue.</Card>;

  const Row = ({ item, children, extra }) => (
    <Card key={item.id} testId={`publishing-item-${item.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-display text-base font-semibold text-slate-900">
            {item.platform} · {item.content_type || "Post"}
          </p>
          <p className="mt-1 line-clamp-2 text-sm text-slate-600">{item.caption || item.title}</p>
          {extra}
        </div>
        <StatusPill status={item.status} testId={`publishing-status-${item.id}`} />
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">{children}</div>
      <div className="mt-3 border-t border-slate-100 pt-3">
        <ManualPostActions item={item} />
        {item.status !== "Published" && (
          <div className="mt-3">
            <MarkAsPosted item={item} onDone={load} />
          </div>
        )}
      </div>
    </Card>
  );

  return (
    <div className="space-y-8" data-testid="publishing-page">
      <PageHeader
        title="Publishing"
        body="Approved content gets a recommended publishing time. Copy the caption, download the image and post it — nothing here goes out without your approval."
      />

      <BetaNotice kind="publishing" testId="publishing-beta-notice" />

      <Card testId="publishing-manual-help">
        <p className="font-display text-base font-semibold text-slate-900">Posting it yourself takes about 30 seconds</p>
        <p className="mt-2 text-sm text-slate-600">
          Every approved post below has a “Copy caption” and “Download photo” button. Copy, open the app, paste, post.
          New to this? The guide walks you through it per platform.
        </p>
        <Link
          to="/dashboard/how-to-post"
          data-testid="publishing-how-to-link"
          className="mt-4 inline-flex rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
        >
          Read the 2-minute guide
        </Link>
      </Card>

      <section data-testid="publishing-approved-section">
        <p className="mb-3 font-display text-lg font-semibold text-slate-900">Approved, not scheduled yet</p>
        {queue.approved_unscheduled.length === 0 ? (
          <Empty testId="publishing-approved-empty" title="Nothing waiting" body="Approve content in My Content and it appears here." />
        ) : (
          <div className="space-y-3">
            {queue.approved_unscheduled.map((item) => (
              <Row key={item.id} item={item}>
                <input
                  type="datetime-local"
                  value={when[item.id] || ""}
                  onChange={(e) => setWhen((w) => ({ ...w, [item.id]: e.target.value }))}
                  data-testid={`publishing-when-${item.id}`}
                  className={`${inputCls} max-w-xs`}
                />
                <Btn
                  disabled={busy === item.id}
                  onClick={() =>
                    act(
                      item.id,
                      () =>
                        api.post(`/social/schedule/${item.id}`, {
                          scheduled_at: when[item.id] ? new Date(when[item.id]).toISOString() : null,
                        }),
                      "Scheduled."
                    )
                  }
                  testId={`publishing-schedule-${item.id}`}
                >
                  {busy === item.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />}
                  {when[item.id] ? "Schedule for this time" : "Use recommended time"}
                </Btn>
                <Btn
                  variant="outline"
                  disabled={busy === item.id}
                  onClick={() => act(item.id, () => api.post(`/social/publish-now/${item.id}`), "Published.")}
                  testId={`publishing-now-${item.id}`}
                >
                  <Send className="h-4 w-4" /> Publish now
                </Btn>
              </Row>
            ))}
          </div>
        )}
      </section>

      <section data-testid="publishing-scheduled-section">
        <p className="mb-3 font-display text-lg font-semibold text-slate-900">Scheduled</p>
        {queue.scheduled.length === 0 ? (
          <Empty testId="publishing-scheduled-empty" title="Nothing scheduled" body="Scheduled posts appear here with their publishing time." />
        ) : (
          <div className="space-y-3">
            {queue.scheduled.map((item) => (
              <Row
                key={item.id}
                item={item}
                extra={
                  <p className="mt-1 text-xs font-semibold text-indigo-700">
                    Publishes {new Date(item.scheduled_at).toLocaleString()} (recommended time)
                  </p>
                }
              >
                <Btn
                  variant="outline"
                  disabled={busy === item.id}
                  onClick={() => act(item.id, () => api.post(`/social/publish-now/${item.id}`), "Published.")}
                  testId={`publishing-now-${item.id}`}
                >
                  <Send className="h-4 w-4" /> Publish now
                </Btn>
              </Row>
            ))}
          </div>
        )}
      </section>

      <section data-testid="publishing-failed-section">
        <p className="mb-3 font-display text-lg font-semibold text-slate-900">Publishing failed</p>
        {queue.failed.length === 0 ? (
          <Empty testId="publishing-failed-empty" title="No failures" body="If a post fails, it shows up here with the reason." />
        ) : (
          <div className="space-y-3">
            {queue.failed.map((item) => (
              <Row
                key={item.id}
                item={item}
                extra={
                  <p className="mt-1 text-xs font-semibold text-red-700" data-testid={`publishing-reason-${item.id}`}>
                    {item.failure_reason} (attempt {item.publish_attempts || 1})
                  </p>
                }
              >
                <Btn
                  disabled={busy === item.id}
                  onClick={() => act(item.id, () => api.post(`/social/retry/${item.id}`), "Retried.")}
                  testId={`publishing-retry-${item.id}`}
                >
                  {busy === item.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                  Retry
                </Btn>
                <Link
                  to="/dashboard/content"
                  data-testid={`publishing-edit-${item.id}`}
                  className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50"
                >
                  Edit post
                </Link>
                <Link
                  to="/dashboard/connections"
                  data-testid={`publishing-reconnect-${item.id}`}
                  className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50"
                >
                  Reconnect account
                </Link>
                <Link
                  to={`/dashboard/support?topic=Technical%20Issue&about=${encodeURIComponent(
                    `${item.platform} post failed: ${item.failure_reason || ""}`
                  )}`}
                  data-testid={`publishing-support-${item.id}`}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50"
                >
                  <LifeBuoy className="h-4 w-4" /> Contact support
                </Link>
              </Row>
            ))}
          </div>
        )}
      </section>

      <section data-testid="publishing-published-section">
        <p className="mb-3 font-display text-lg font-semibold text-slate-900">Published</p>
        {queue.published.length === 0 ? (
          <Empty testId="publishing-published-empty" title="Nothing published yet" body="Published posts are listed here." />
        ) : (
          <div className="space-y-3">
            {queue.published.slice(0, 20).map((item) => (
              <Row
                key={item.id}
                item={item}
                extra={
                  <p className="mt-1 text-xs text-slate-500">
                    Published {new Date(item.published_at).toLocaleString()}
                    {item.posted_manually ? " · posted by you" : item.simulated ? " · simulated" : ""}
                    {item.external_post_url ? (
                      <>
                        {" · "}
                        <a
                          href={item.external_post_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`published-link-${item.id}`}
                          className="font-semibold text-[#F95738] hover:underline"
                        >
                          View post
                        </a>
                      </>
                    ) : null}
                  </p>
                }
              >
                {item.posted_manually && <UndoMarkPosted item={item} onDone={load} />}
              </Row>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
